/** KPI value ↔ physical dial position. Pure, linear, shared by the device API and the live preview. */
import type { NeedleGauge } from './cluster-model.ts';
import type { NeedleSettings } from './settings.ts';

export interface DialPosition {
  /** Position in the dial's printed units (e.g. 142.5 on a 0–260 km/h speedometer). */
  dial: number;
  /** 0 = dial min, 1 = dial max. */
  fraction: number;
  clamped: 'low' | 'high' | null;
}

type Mapping = Pick<NeedleSettings, 'kpi_min' | 'kpi_max'>;
type Dial = Pick<NeedleGauge, 'min' | 'max'>;

/** Unclamped fraction of the KPI range (0 at kpi_min, 1 at kpi_max). Works for inverted ranges. */
export function kpiFraction(value: number, m: Mapping): number {
  return (value - m.kpi_min) / (m.kpi_max - m.kpi_min);
}

export function mapToDial(value: number, m: Mapping, gauge: Dial): DialPosition {
  if (!Number.isFinite(value)) return { dial: gauge.min, fraction: 0, clamped: 'low' };
  const raw = kpiFraction(value, m);
  const fraction = Math.min(1, Math.max(0, raw));
  const clamped = raw < 0 ? 'low' : raw > 1 ? 'high' : null;
  return { dial: round(gauge.min + fraction * (gauge.max - gauge.min), 4), fraction, clamped };
}

/** Inverse: which KPI value sits at a given dial position (used to label ticks). */
export function dialToKpi(dial: number, m: Mapping, gauge: Dial): number {
  const fraction = (dial - gauge.min) / (gauge.max - gauge.min);
  return m.kpi_min + fraction * (m.kpi_max - m.kpi_min);
}

/** Needle angle in degrees for the preview (0 = 12 o'clock, clockwise). */
export function dialToAngle(dial: number, gauge: Pick<NeedleGauge, 'min' | 'max' | 'sweep_deg' | 'start_deg'>): number {
  const fraction = (dial - gauge.min) / (gauge.max - gauge.min);
  return gauge.start_deg + fraction * gauge.sweep_deg;
}

export interface Tick {
  dial: number;
  major: boolean;
  /** KPI value at this tick divided by the multiplier, formatted for the dial face. */
  label: string | null;
}

/** Tick marks for a custom dial face: printed dial steps relabelled with KPI values. */
export function ticks(gauge: NeedleGauge, m: Pick<NeedleSettings, 'kpi_min' | 'kpi_max' | 'multiplier'>): Tick[] {
  const out: Tick[] = [];
  const step = gauge.minor_step ?? gauge.major_step;
  const lo = Math.min(gauge.min, gauge.max);
  const hi = Math.max(gauge.min, gauge.max);
  const count = Math.round((hi - lo) / step);
  for (let i = 0; i <= count; i++) {
    const dial = round(lo + i * step, 6);
    const major = isMultiple(dial - lo, gauge.major_step);
    const kpi = dialToKpi(dial, m, gauge) / m.multiplier;
    out.push({ dial, major, label: major ? formatTick(kpi) : null });
  }
  return out;
}

/** "×1000" style label for the dial face, or '' when the multiplier is 1. */
export function multiplierLabel(multiplier: number): string {
  return multiplier === 1 ? '' : `×${formatTick(multiplier)}`;
}

export function formatTick(v: number): string {
  const r = round(v, 2);
  return Number.isInteger(r) ? String(r) : String(r).replace(/0+$/, '');
}

function isMultiple(v: number, step: number) {
  const q = v / step;
  return Math.abs(q - Math.round(q)) < 1e-6;
}

export function round(v: number, digits: number) {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
}
